import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Badge from "@mui/material/Badge";
import Menu from "@mui/material/Menu";
import useMediaQuery from "@mui/material/useMediaQuery";
import InfiniteScroll from "react-infinite-scroll-component";
import useHideOnScroll from "../../hooks/useHideOnScroll";
import CustomizationSettings from "../DashboardNavbar/CustomizationSettings";
import SoftBox from "../SoftBox";
import SoftTypography from "../SoftTypography";
import SoftButton from "../SoftButton";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import NotificationsPausedIcon from "@mui/icons-material/NotificationsPaused";
import {
  getUserNotification,
  updateAllNotificationStatus,
  updateNotificationReadStatus,
  updateNotificationStatus,
  addNotification,
} from "../../redux/notifications/actions";
import socketConstants from "../../constants/socket";
import socket from "../../configs/socket";
import { UseUserDetails } from "../../hooks/auth";
import { UseCompanyColors } from "../../hooks/company";
import { getNotificationTitle, getNotiVisual } from "../../helpers/notifications";
import { popNotificationToast } from "../Notifications/NotificationToast";

// Notifications carry a pre-formatted relative string ("Just now", "42 min
// ago") rather than a raw timestamp, so "Today" vs "Earlier" is inferred from
// that text instead of re-deriving it from a date the API doesn't send.
function isRecent(timeElapsed = "") {
  return (
    /\b(just now|sec|min|hour|hr)\b/i.test(timeElapsed) &&
    !/\b(day|week|month|yr|year)\b/i.test(timeElapsed)
  );
}
function groupByRecency(items) {
  const today = items.filter((item) => isRecent(item?.timeElapsed));
  const earlier = items.filter((item) => !isRecent(item?.timeElapsed));
  return [
    ["Today", today],
    ["Earlier", earlier],
  ];
}

function TopBar({ activeWorkspaceName, isGlobalView, onOpenSettings, onOpenSidebar, sidebarOffset = 0 }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { SOCKET_EVENT } = socketConstants;
  const currentUser = UseUserDetails();
  const { primaryColor: companyPrimaryColor } = UseCompanyColors();

  const { notifications, unreadNotifications } = useSelector((state) => state.notificationsReducer);
  const { chats } = useSelector((state) => state.chatsReducer);

  const [openMenu, setOpenMenu] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);
  const [allNotification, setAllNotification] = useState(false);

  // The dropdown's pointer must line up with the bell icon's actual screen
  // position, not the menu paper's edge — MUI's Popover shifts the paper
  // horizontally to keep it on-screen (marginThreshold), which decouples the
  // paper's right edge from the anchor whenever there isn't enough room.
  const notifButtonRef = useRef(null);
  const notifPaperRef = useRef(null);
  const [arrowLeft, setArrowLeft] = useState(null);

  const updateArrowPosition = useCallback(() => {
    if (!notifButtonRef.current || !notifPaperRef.current) return;
    const btnRect = notifButtonRef.current.getBoundingClientRect();
    const paperRect = notifPaperRef.current.getBoundingClientRect();
    setArrowLeft(btnRect.left + btnRect.width / 2 - paperRect.left);
  }, []);

  useLayoutEffect(() => {
    if (!openMenu) return;
    updateArrowPosition();
    window.addEventListener("resize", updateArrowPosition);
    return () => window.removeEventListener("resize", updateArrowPosition);
  }, [openMenu, updateArrowPosition]);

  // Facebook-style auto-hide: slides off-screen on scroll down, reappears on
  // any scroll up. Applies on every breakpoint — the BottomNav has its own,
  // separate mobile-only instance of this same hook and is unaffected.
  const isMobileView = useMediaQuery("(max-width:767px)");
  const hidden = useHideOnScroll(8, true);

  useEffect(() => {
    if (currentUser?._id) dispatch(getUserNotification(currentUser._id));
  }, [currentUser?._id, dispatch]);

  const handleCloseMenu = () => setOpenMenu(null);
  const handleOpenMenu = (e) => setOpenMenu(openMenu ? null : e.currentTarget);
  const markAllReadTrigger = () => dispatch(updateAllNotificationStatus({ userId: currentUser._id, is_read: true }));
  const markAllUnReadTrigger = () => dispatch(updateNotificationReadStatus(currentUser._id));

  const handleNotiClick = (item) => {
    handleCloseMenu();
    dispatch(updateNotificationStatus(item.notiId));
    if (item.action === "badge") {
      navigate("/settings/level-up", { state: { item, showPopup: true } });
    } else if (item.action === "referral") {
      navigate("/affiliates");
    } else if (item.action === "chat") {
      const elIndex = chats?.findIndex((c) => c._id === item.chat) ?? -1;
      navigate("/messages", { state: { chatToSelect: chats?.[elIndex] } });
    } else if (item.action === "playbook") {
      navigate("/playbook");
    } else if (item.action === "performance-alert") {
      if (item.link) window.location.href = item.link;
    } else {
      // "Where the notification came from": the post itself, comments open
      // when it's about a comment/like. Reviews and Yard Sign posts have no
      // single-item permalink — they only exist inline in the main Team Feed
      // list, so FeedPage scrolls to and briefly highlights the right card
      // instead (see scrollToId handling there).
      const openComments = item.action === "comment" || item.action === "comment-thread" || item.action === "comment-like";
      if (item.targetType && item.targetType !== "Job") {
        navigate("/feed", { state: { scrollToId: item.job, scrollToType: item.targetType, openComments } });
      } else {
        navigate(`/feed/${item?.job}`, { state: { openComments } });
      }
    }
  };

  // Every real-time notification both lands in the persisted dropdown
  // (dispatch) and pops a transient top-right toast so it's noticed the
  // moment it arrives, not just whenever the bell happens to get clicked.
  const notifyIncoming = useCallback(
    (data) => {
      dispatch(addNotification(data));
      popNotificationToast(data, () => handleNotiClick(data));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch],
  );

  useEffect(() => {
    socket.on(SOCKET_EVENT.SEND_COMMENT_NOTI, notifyIncoming);
    return () => socket.off(SOCKET_EVENT.SEND_COMMENT_NOTI, notifyIncoming);
  }, [notifyIncoming, SOCKET_EVENT.SEND_COMMENT_NOTI]);

  useEffect(() => {
    socket.on(SOCKET_EVENT.SEND_SLOW_CLAP_NOTI, notifyIncoming);
    return () => socket.off(SOCKET_EVENT.SEND_SLOW_CLAP_NOTI, notifyIncoming);
  }, [notifyIncoming, SOCKET_EVENT.SEND_SLOW_CLAP_NOTI]);

  useEffect(() => {
    socket.on(SOCKET_EVENT.SEND_COMMENT_LIKE_NOTI, notifyIncoming);
    return () => socket.off(SOCKET_EVENT.SEND_COMMENT_LIKE_NOTI, notifyIncoming);
  }, [notifyIncoming, SOCKET_EVENT.SEND_COMMENT_LIKE_NOTI]);

  useEffect(() => {
    socket.on(SOCKET_EVENT.BADGE_COMPLETED, notifyIncoming);
    return () => socket.off(SOCKET_EVENT.BADGE_COMPLETED, notifyIncoming);
  }, [notifyIncoming, SOCKET_EVENT.BADGE_COMPLETED]);

  useEffect(() => {
    socket.on(SOCKET_EVENT.REFERRAL_SIGN_UP, notifyIncoming);
    return () => socket.off(SOCKET_EVENT.REFERRAL_SIGN_UP, notifyIncoming);
  }, [notifyIncoming, SOCKET_EVENT.REFERRAL_SIGN_UP]);

  useEffect(() => {
    socket.on(SOCKET_EVENT.AUTOMATION_NOTIFICATION, notifyIncoming);
    return () => socket.off(SOCKET_EVENT.AUTOMATION_NOTIFICATION, notifyIncoming);
  }, [notifyIncoming, SOCKET_EVENT.AUTOMATION_NOTIFICATION]);

  const titleText = isGlobalView ? "Company" : (activeWorkspaceName || "Workspace");
  const unreadCount = Array.isArray(unreadNotifications) ? unreadNotifications.length : 0;

  const renderNotificationMenu = () => {
    const activeList = allNotification ? unreadNotifications : notifications;
    const showEmptyState =
      (allNotification && !unreadNotifications.length) ||
      (!allNotification && !notifications.length);

    return (
      <InfiniteScroll dataLength={3} hasMore scrollableTarget="scrollableDivTopBar">
        <Menu
          anchorEl={openMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          open={Boolean(openMenu)}
          onClose={handleCloseMenu}
          TransitionProps={{ onEntered: updateArrowPosition }}
          sx={{ mt: 1.5 }}
          PaperProps={{
            ref: notifPaperRef,
            style: { width: "min(340px, calc(100vw - 24px))", borderRadius: 16, padding: 0, overflow: "visible" },
            sx: {
              "&::before": {
                content: '""',
                position: "absolute",
                top: -7,
                left: arrowLeft != null ? `${arrowLeft - 7}px` : undefined,
                right: arrowLeft != null ? undefined : 18,
                width: 0,
                height: 0,
                borderLeft: "7px solid transparent",
                borderRight: "7px solid transparent",
                borderBottom: "7px solid #ffffff",
                filter: "drop-shadow(0 -1px 1px rgba(15,23,42,0.06))",
              },
            },
          }}
        >
          <div className="px-4 pt-4 pb-2 flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#EAF0FE" }}>
              <NotificationsIcon sx={{ fontSize: 16, color: "#2563EB" }} />
            </div>
            <div className="min-w-0">
              <SoftTypography sx={{ fontSize: "14px", fontWeight: 700, lineHeight: 1.2, display: "block" }}>
                Notifications
              </SoftTypography>
              <SoftTypography sx={{ fontSize: "11px", color: "#94A3B8" }}>
                {unreadCount > 0 ? `${unreadCount} unread` : "You're all caught up"}
              </SoftTypography>
            </div>
          </div>

          <div className="px-4 pb-2">
            <div className="flex w-full bg-gray-100 rounded-full p-0.5">
              <button
                type="button"
                onClick={() => setAllNotification(false)}
                className={`flex-1 py-1 rounded-full text-xs font-semibold transition-colors ${!allNotification ? "text-white" : "text-gray-500"}`}
                style={!allNotification ? { backgroundColor: companyPrimaryColor || "#1E1B4B" } : undefined}
              >
                All
              </button>
              <button
                type="button"
                onClick={() => setAllNotification(true)}
                className={`flex-1 py-1 rounded-full text-xs font-semibold transition-colors ${allNotification ? "text-white" : "text-gray-500"}`}
                style={allNotification ? { backgroundColor: companyPrimaryColor || "#1E1B4B" } : undefined}
              >
                Unread
              </button>
            </div>
          </div>

          <div className="max-h-[280px] overflow-y-auto pb-1">
            {showEmptyState ? (
              <div className="flex flex-col items-center py-5 px-4">
                <NotificationsPausedIcon fontSize="small" style={{ fontSize: 28, color: "#CBD5E1" }} />
                <SoftTypography sx={{ fontSize: "12px", fontWeight: 600, mt: 1, color: "#94A3B8" }}>
                  You have no notifications
                </SoftTypography>
              </div>
            ) : (
              groupByRecency(activeList).map(([label, items]) =>
                items.length ? (
                  <div key={label}>
                    <div className="px-4 pt-1.5 pb-0.5">
                      <SoftTypography
                        sx={{ fontSize: "10px", color: "#94A3B8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}
                      >
                        {label}
                      </SoftTypography>
                    </div>
                    {items.map((item) => {
                      const visual = getNotiVisual(item);
                      const Icon = visual.icon;
                      return (
                        <div
                          key={item?.notiId ?? `${label}-${item?.sender}-${item?.timeElapsed}`}
                          onClick={() => handleNotiClick(item)}
                          className="group relative mx-2 mb-1.5 rounded-lg flex items-start gap-2 p-2 cursor-pointer hover:bg-gray-50 border-l-4"
                          style={{
                            borderLeftColor: visual.color,
                            backgroundColor: item?.is_read ? "transparent" : `${visual.color}0D`,
                          }}
                        >
                          <div
                            className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: visual.bg }}
                          >
                            <Icon sx={{ fontSize: 14, color: visual.color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <SoftTypography sx={{ fontSize: "12px", fontWeight: 700, display: "block" }}>
                              {getNotificationTitle(item)}
                            </SoftTypography>
                            <SoftTypography sx={{ fontSize: "10.5px", color: "#94A3B8" }}>
                              {item?.timeElapsed}
                            </SoftTypography>
                          </div>
                          {!item?.is_read && (
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1 flex-shrink-0" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : null,
              )
            )}
          </div>

          {allNotification && unreadNotifications.length ? (
            <SoftBox sx={{ display: "flex", justifyContent: "center", py: 1, borderTop: "1px solid #F1F5F9" }}>
              <SoftButton color="dark" size="small" sx={{ fontSize: "11px", py: 0.5 }} onClick={markAllReadTrigger}>Mark all as read</SoftButton>
            </SoftBox>
          ) : null}
          {!allNotification && notifications.length ? (
            <SoftBox sx={{ display: "flex", justifyContent: "center", py: 1, borderTop: "1px solid #F1F5F9" }}>
              <SoftButton color="dark" size="small" sx={{ fontSize: "11px", py: 0.5 }} onClick={markAllUnReadTrigger}>Make all as unread</SoftButton>
            </SoftBox>
          ) : null}
        </Menu>
      </InfiniteScroll>
    );
  };

  return (
    <>
      {/* `fixed` + an explicit spacer below, not `sticky` — a sticky element
          that also carries its own translateY (for the hide-on-scroll slide)
          can repaint at a stale position on mobile WebKit during a scroll-
          direction change, so the bar and whatever page content had scrolled
          into that 56px band both end up momentarily painted on screen at
          once. `fixed` is composited independently of document scroll, so
          there's nothing underneath it left to bleed through. */}
      <header
        id="gmb-topbar"
        className="h-14 bg-white border-b border-gray-100 flex items-center justify-between px-4 sm:px-8 fixed top-0 right-0 z-20 shadow-sm"
        style={{
          left: sidebarOffset,
          transform: hidden ? "translateY(-100%)" : "translateY(0)",
          transition: "transform 0.3s ease-in-out, left 0.3s ease-in-out",
          willChange: "transform",
        }}
      >
        <div className="flex items-center gap-3 min-w-0">
          {typeof onOpenSidebar === "function" && (
            <button
              type="button"
              onClick={onOpenSidebar}
              className="p-2 rounded-xl text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors flex-shrink-0"
              aria-label="Open menu"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          )}
          <div className="flex flex-col min-w-0">
            {!isGlobalView && (
              <span className="text-[10px] font-bold text-gray-400 tracking-tight uppercase tracking-widest">Workspace</span>
            )}
            <span className="text-[13px] font-semibold text-gray-900 truncate">{titleText}</span>
          </div>
          {/* Left portal — Homepage parks the "Editing dashboard" chip here. */}
          <div id="gmbw-topbar-slot-left" className="flex items-center min-w-0" />
        </div>

        <div className="flex items-center space-x-6">
          {/* Portal target for page-scoped actions. The customizable dashboard
              portals its period picker and Customize/Publish here; empty
              elsewhere. Same id as the slot Homepage expects. */}
          <div id="gmbw-topbar-slot" className="flex items-center" />
          {/* Persistent icons — hidden while the dashboard is in edit mode
              (body.gmb-dash-editing) so edit controls own the bar. */}
          <span className="gmb-topbar-persist flex items-center gap-2">
          <button
            type="button"
            id="scrollableDivTopBar"
            ref={notifButtonRef}
            onClick={handleOpenMenu}
            className="relative py-2 px-1 rounded-xl transition-colors hover:bg-gray-50"
            aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : "Notifications"}
          >
            {/* Unread count is the only indicator here — a plain dot used to be
                layered in the same corner underneath it, which is what made
                the badge read as "overlapping the icon" rather than sitting
                just outside it. */}
            <Badge
              badgeContent={unreadCount}
              color="error"
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: isMobileView ? "9px" : "10px",
                  minWidth: isMobileView ? "14px" : "16px",
                  height: isMobileView ? "14px" : "16px",
                  padding: 0,
                  // Nudge the badge further past the icon's corner (MUI's
                  // default centers it exactly on the corner, so half of it
                  // sits on top of the bell glyph on an icon this small).
                  top: -2,
                  right: -2,
                },
              }}
            >
              {/* Explicit width/height (not just Tailwind's w-5/h-5) so this
                  MUI icon can't lose a class-order fight with the gear's
                  plain <svg> below — both need to resolve to the same 20px
                  box regardless of which stylesheet loads last. */}
              <NotificationsOutlinedIcon
                className="text-gray-400 hover:text-gray-600"
                sx={{ fontSize: 20, width: "20px", height: "20px" }}
              />
            </Badge>
          </button>

          <button
            type="button"
            className="py-2 px-1 cursor-pointer group hover:bg-indigo-50 rounded-xl transition-all"
            onClick={() => (onOpenSettings ? onOpenSettings() : setOpenSettings(true))}
            title="Customization Settings"
            aria-label="Customization Settings"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              className="text-gray-400 group-hover:text-[#7C3AED] transition-colors"
              style={{ width: "20px", height: "20px" }}
            >
              <path d="M10.343 3.94c.09-.542.56-.94 1.11-.94h1.093c.55 0 1.02.398 1.11.94l.149.894c.07.424.384.764.78.93.398.164.855.142 1.205-.108l.737-.527a1.125 1.125 0 0 1 1.45.12l.773.774c.39.389.44 1.002.12 1.45l-.527.737c-.25.35-.272.806-.107 1.204.165.397.505.71.93.78l.893.15c.543.09.94.559.94 1.109v1.094c0 .55-.397 1.02-.94 1.11l-.894.149c-.424.07-.764.383-.929.78-.165.398-.143.854.107 1.204l.527.738c.32.447.269 1.06-.12 1.45l-.774.773a1.125 1.125 0 0 1-1.449.12l-.738-.527c-.35-.25-.806-.272-1.203-.107-.398.165-.71.505-.781.929l-.149.894c-.09.542-.56.94-1.11.94h-1.094c-.55 0-1.019-.398-1.11-.94l-.148-.894c-.071-.424-.384-.764-.781-.93-.398-.164-.854-.142-1.204.108l-.738.527c-.447.32-1.06.269-1.45-.12l-.773-.774a1.125 1.125 0 0 1-.12-1.45l.527-.737c.25-.35.272-.806.108-1.204-.165-.397-.506-.71-.93-.78l-.894-.15c-.542-.09-.94-.56-.94-1.109v-1.094c0-.55.398-1.02.94-1.11l.894-.149c.424-.07.765-.383.93-.78.165-.398.143-.854-.108-1.204l-.526-.738a1.125 1.125 0 0 1 .12-1.45l.773-.773a1.125 1.125 0 0 1 1.45-.12l.737.527c.35.25.807.272 1.204.107.397-.165.71-.505.78-.929l.15-.894z" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          </span>
        </div>
      </header>
      {/* The 56px TopBar no longer claims from flow (it's `fixed`) is
          reserved by the page content wrapper (Main.jsx paddingTop), not
          here — TopBar renders as a flex sibling of that wrapper now, so a
          spacer div in this fragment would sit beside it, not above it. */}

      {renderNotificationMenu()}
      <CustomizationSettings open={openSettings} handleClose={() => setOpenSettings(false)} dark />
    </>
  );
}

export default TopBar;
